import './DevicePlaylists.scss';
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { deviceStore } from '../../store';
import { ROUTES, RESPONSE} from '../../js/_constants';
import { useTranslation } from 'react-i18next';
import helpers from '../../js/helpers';
import ItemCard from '../ItemCard/ItemCard';
import Loader from '../loader/Loader';
import { type Playlist } from '../../api/types/index';
import { isEmpty } from 'ramda';
import { type ActionButtonType } from '../ItemCard/ItemCard';
import IconButton from '../iconButton/IconButton';

const NoPlaylistsText = () => {
    const { t } = useTranslation();
    return <p className="no-playlists"> {t('device_detail_no_recently_assigned_playlists')}</p>;
};

type PlaylistCardProps = {
    name?: string;
    className?: string;
    playlist: Playlist;
    buttons: Array<ActionButtonType | Element | React.ReactNode>;
    rest?: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    >;
};

export const PlaylistCard: React.FC<PlaylistCardProps> = ({
    className,
    playlist,
    buttons,
    name,
    ...rest
}) => {
    const { t } = useTranslation();
    return (
        <>
            <ItemCard
                className={cx('b-device-playlists__playlist-card', className)}
                key={playlist.uuid + className}
                name={name}
                href={`${ROUTES.PLAYLISTS}/${playlist.uuid}`}
                thumbnail={playlist.thumbnail}
                icon="media-playlist"
                title={playlist.title}
                subtitle={`${
                    playlist.language || 'EN'
                } - ${helpers.humanDuration(playlist.duration)} Min`}
                description={playlist.description}
                footer={`${t('playlist_last_edit')} : ${
                    playlist.updatedAt
                        ? helpers.humanDate(playlist.updatedAt)
                        : ''
                }`}
                buttons={buttons}
                {...rest}
            />
        </>
    );
};

type DevicePlaylistsProps = {
    items: Playlist[];
    iri;
};

const DevicePlaylists = ({
    items,
    iri
}: DevicePlaylistsProps) => {
    const { t } = useTranslation();

    const { updateDevice, selected, loading } = deviceStore();

    const [assignedPlaylists, setAssignedPlaylists] = useState<Playlist[]>([]);

    useEffect(() => {
        setAssignedPlaylists(items);
    }, [items]);
 
    const handleTogglePlaylistAssignment = (playlistIri: string) => {
        const nextPlaylists = [
            ...selected.playlists.map((playlist,idx) => {return {position: idx + 1, contentPlaylist:playlist.contentPlaylist[RESPONSE.ID]}}),
            { position: selected.playlists.length + 1, contentPlaylist: playlistIri }
        ];
        updateDevice({
            id: iri,
            body: {
                playlists: nextPlaylists
            }
        });
    };

    const checkIfAssigned = id =>
        selected.playlists.map(playlist => playlist.contentPlaylist[RESPONSE.ID]).includes(id);

    const generatePlaylistActionButtons = (id, index) => [
        <IconButton
            key={id}
            className="assign-icon"
            icon="sync-large"
            onClick={() => handleTogglePlaylistAssignment(id)}
            disabled={checkIfAssigned(id)}
            name={`assign-recent-playlist-button-${index + 1}`}
        />
    ];

    return (
        <>
            <div
                className={cx('b-device-playlists', {
                    'is-disabled': loading
                })}
            >
                    <h3 className="playlists-header">
                    {t('device_detail_recently_assigned_playlists')}
                    </h3>
                    {loading && <Loader />}
                    {isEmpty(assignedPlaylists) && <NoPlaylistsText />}
                    {assignedPlaylists.map((playlist, index) => (
                        <PlaylistCard
                            name={`recent-playlist-card-${index + 1}`}
                            key={index}
                            playlist={playlist.contentPlaylist}
                            buttons={generatePlaylistActionButtons(
                                playlist.contentPlaylist[RESPONSE.ID], index
                            )}
                        />
           
                    ))}
            </div>
        </>
    );
};

export default DevicePlaylists;
